import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import membershipService from '../services/membershipService';
import { checkSubscriptionExpiry } from '../utils/membershipGates';

const SubscriptionContext = createContext(null);

// Default plan features for users without subscription (Basic plan)
// MEMBERSHIP PLAN RULES:
// 
// 1. Received Buy Leads / RFQs:
//    - Basic: RFQs can be sent to companies from ANY country
//    - GCC: RFQs can ONLY be sent to GCC companies (error if non-GCC)
//    - Global: RFQs can be sent to any company in any country
//
// 2. View Buyer Contact Details:
//    - Basic: NO contact details visible
//    - GCC: ONLY GCC country contact details visible
//    - Global: ALL contact details visible
//
// 3. Messaging:
//    - Basic: CANNOT message anyone
//    - GCC: CANNOT message anyone
//    - Global: CAN message everyone
//
const DEFAULT_PLAN_FEATURES = {
  productPostingLimit: 10,
  canReceiveRFQs: true, // ✓ Can receive RFQs from any country
  rfqRegion: 'global', // Basic receives RFQs from any country
  canViewBuyerContacts: false, // ✗ Cannot view ANY contact details
  buyerContactRegion: null,
  canReceiveDirectCalls: false, // ✗
  buyerDirectoryAccess: false, // ✗
  buyerDirectoryRegion: null,
  canUploadVideos: false, // ✗
  maxProductImages: 5,
  hasCompanyProfile: true, // ✓
  canLiveChat: false, // ✗ CANNOT message anyone
  canHighlightBrand: false, // ✗
  hasDashboardAccess: true, // ✓
  canAddProfessional: false, // ✗
  canSubmitProjects: false, // ✗
  canPostNews: false, // ✗
  trustedBadgeAvailable: false // ✗
};

// Polling interval for checking plan updates (5 minutes)
const PLAN_UPDATE_CHECK_INTERVAL = 5 * 60 * 1000;

export const SubscriptionProvider = ({ children }) => {
  const { user } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [planFeatures, setPlanFeatures] = useState(DEFAULT_PLAN_FEATURES);
  const [planName, setPlanName] = useState('basic');
  const [planDisplayName, setPlanDisplayName] = useState('Basic');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expiryStatus, setExpiryStatus] = useState({
    isExpiring: false,
    isExpired: false,
    daysRemaining: null,
    expiryDate: null
  });
  
  // Track the last known plan update timestamp to detect changes
  const lastPlanUpdatedAt = useRef(null);
  const pollIntervalRef = useRef(null);

  // Fetch user subscription when user changes
  const fetchSubscription = useCallback(async (forceRefresh = false) => {
    if (!user?.id) {
      setSubscription(null);
      setPlanFeatures(DEFAULT_PLAN_FEATURES);
      setPlanName('basic');
      setPlanDisplayName('Basic');
      setLoading(false);
      lastPlanUpdatedAt.current = null;
      return;
    }

    try {
      // Only show loading on initial fetch, not on background refreshes
      if (!forceRefresh) {
        setLoading(true);
      }
      setError(null);
      
      // Use the new API endpoint that fetches features directly from database
      // This ensures permissions are always fresh and dynamic
      const response = await membershipService.getUserPlanFeatures(user.id);
      
      if (response && response.planName) {
        // API returns: { hasSubscription, planName, planDisplayName, features: {...}, planUpdatedAt }
        // Extract the features object from the response
        const features = response.features || {};
        
        // Track the plan's last update timestamp for change detection
        const newUpdatedAt = response.planUpdatedAt ? new Date(response.planUpdatedAt).getTime() : null;
        
        // Only update state if this is initial load or plan has been updated
        const hasChanged = !lastPlanUpdatedAt.current || 
                          newUpdatedAt !== lastPlanUpdatedAt.current ||
                          !forceRefresh;
        
        if (hasChanged) {
          lastPlanUpdatedAt.current = newUpdatedAt;
          
          setPlanName(response.planName);
          setPlanDisplayName(response.planDisplayName || response.planName);
          setPlanFeatures({
            productPostingLimit: features.productPostingLimit ?? DEFAULT_PLAN_FEATURES.productPostingLimit,
            canReceiveRFQs: features.canReceiveRFQs ?? DEFAULT_PLAN_FEATURES.canReceiveRFQs,
            rfqRegion: features.rfqRegion ?? DEFAULT_PLAN_FEATURES.rfqRegion,
            canViewBuyerContacts: features.canViewBuyerContacts ?? DEFAULT_PLAN_FEATURES.canViewBuyerContacts,
            buyerContactRegion: features.buyerContactRegion ?? DEFAULT_PLAN_FEATURES.buyerContactRegion,
            canReceiveDirectCalls: features.canReceiveDirectCalls ?? DEFAULT_PLAN_FEATURES.canReceiveDirectCalls,
            buyerDirectoryAccess: features.buyerDirectoryAccess ?? DEFAULT_PLAN_FEATURES.buyerDirectoryAccess,
            buyerDirectoryRegion: features.buyerDirectoryRegion ?? DEFAULT_PLAN_FEATURES.buyerDirectoryRegion,
            canUploadVideos: features.canUploadVideos ?? DEFAULT_PLAN_FEATURES.canUploadVideos,
            maxProductImages: features.maxProductImages ?? DEFAULT_PLAN_FEATURES.maxProductImages,
            hasCompanyProfile: features.hasCompanyProfile ?? DEFAULT_PLAN_FEATURES.hasCompanyProfile,
            canLiveChat: features.canLiveChat ?? DEFAULT_PLAN_FEATURES.canLiveChat,
            canHighlightBrand: features.canHighlightBrand ?? DEFAULT_PLAN_FEATURES.canHighlightBrand,
            hasDashboardAccess: features.hasDashboardAccess ?? DEFAULT_PLAN_FEATURES.hasDashboardAccess,
            canAddProfessional: features.canAddProfessional ?? DEFAULT_PLAN_FEATURES.canAddProfessional,
            canSubmitProjects: features.canSubmitProjects ?? DEFAULT_PLAN_FEATURES.canSubmitProjects,
            canPostNews: features.canPostNews ?? DEFAULT_PLAN_FEATURES.canPostNews,
            trustedBadgeAvailable: features.trustedBadgeAvailable ?? DEFAULT_PLAN_FEATURES.trustedBadgeAvailable
          });
          
          // Store hasSubscription for reference
          setSubscription(response.hasSubscription ? response : null);
          
          // Check subscription expiry status
          if (response.subscriptionEndDate) {
            const expiry = checkSubscriptionExpiry(response.subscriptionEndDate, 7);
            setExpiryStatus(expiry);
          } else {
            setExpiryStatus({
              isExpiring: false,
              isExpired: false,
              daysRemaining: null,
              expiryDate: null
            });
          }
          
          // Log when plan features are refreshed (for debugging)
          if (forceRefresh) {
            console.log('SubscriptionContext: Plan features refreshed for user', user.id, '- Plan:', response.planName);
          }
        }
      } else {
        // No features returned - use defaults
        setSubscription(null);
        setPlanFeatures(DEFAULT_PLAN_FEATURES);
        setPlanName('basic');
        setPlanDisplayName('Basic');
        lastPlanUpdatedAt.current = null;
        setExpiryStatus({
          isExpiring: false,
          isExpired: false,
          daysRemaining: null,
          expiryDate: null
        });
      }
    } catch (err) {
      // Log the error for debugging
      console.error('SubscriptionContext: Error fetching plan features for user', user?.id, ':', err);
      // Fall back to basic plan on error
      setSubscription(null);
      setPlanFeatures(DEFAULT_PLAN_FEATURES);
      setPlanName('basic');
      setPlanDisplayName('Basic');
      lastPlanUpdatedAt.current = null;
      setExpiryStatus({
        isExpiring: false,
        isExpired: false,
        daysRemaining: null,
        expiryDate: null
      });
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  // Initial fetch when user changes
  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  // Periodic polling to detect admin plan updates while user is logged in
  // This ensures suppliers see updated plan features without needing to re-login
  useEffect(() => {
    if (!user?.id) {
      // Clear any existing interval when user logs out
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      return;
    }

    // Set up periodic polling for plan updates
    pollIntervalRef.current = setInterval(() => {
      // Silent refresh - don't show loading state
      fetchSubscription(true);
    }, PLAN_UPDATE_CHECK_INTERVAL);

    // Cleanup on unmount or when user changes
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [user?.id, fetchSubscription]);

  // Also refresh plan features when window regains focus
  // This catches updates when user has been away from the tab
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && user?.id) {
        // Silent refresh when tab becomes visible
        fetchSubscription(true);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user?.id, fetchSubscription]);

  // Check if user has a specific permission
  const hasPermission = useCallback((permissionKey) => {
    // IMPORTANT: Admins have full access to all features - bypass plan restrictions
    if (user?.adminRole === 'admin' || user?.adminRole === 'subadmin') {
      return true;
    }
    
    const value = planFeatures[permissionKey];
    // For boolean permissions
    if (typeof value === 'boolean') {
      return value;
    }
    // For numeric permissions (like productPostingLimit), return true if > 0
    if (typeof value === 'number') {
      return value > 0;
    }
    // For string permissions (like rfqRegion), return true if not null
    return value !== null && value !== undefined;
  }, [planFeatures, user?.adminRole]);

  // Get a specific feature value
  const getFeature = useCallback((featureKey) => {
    return planFeatures[featureKey];
  }, [planFeatures]);

  // Check if user has an active paid subscription
  const isPaidSubscriber = useCallback(() => {
    return subscription !== null && planName !== 'basic';
  }, [subscription, planName]);

  // Check if current user is admin - admins bypass ALL membership restrictions
  const isAdmin = useCallback(() => {
    return user?.adminRole === 'admin' || user?.adminRole === 'subadmin';
  }, [user?.adminRole]);

  // Check if contacts should be visible based on plan and region
  // View Buyer Contact Details Rules:
  // - Admin: ALL contacts visible (bypass all restrictions)
  // - Basic Plan: NO contact details visible (canViewBuyerContacts=false)
  // - GCC Plan: ONLY GCC country contacts visible (buyerContactRegion='gcc')
  // - Global Plan: ALL contacts visible (buyerContactRegion='all')
  const canViewContactsForRegion = useCallback((buyerCountry) => {
    // IMPORTANT: Admins have full access - bypass all restrictions
    if (user?.adminRole === 'admin' || user?.adminRole === 'subadmin') {
      return true;
    }

    // Basic plan - cannot view any contacts
    if (!planFeatures.canViewBuyerContacts) {
      return false;
    }
    
    const region = planFeatures.buyerContactRegion;
    
    // Global plan - can view all contacts
    if (region === 'all' || region === 'global') {
      return true;
    }
    
    // GCC plan - can only view GCC country contacts
    if (region === 'gcc') {
      // GCC Countries: UAE, Saudi Arabia, Qatar, Kuwait, Bahrain, Oman
      const GCC_COUNTRIES = [
        'UAE', 'United Arab Emirates', 'AE',
        'Saudi Arabia', 'KSA', 'SA',
        'Qatar', 'QA',
        'Kuwait', 'KW',
        'Bahrain', 'BH',
        'Oman', 'OM'
      ];
      
      if (!buyerCountry) return false;
      
      return GCC_COUNTRIES.some(c => 
        buyerCountry.toLowerCase().includes(c.toLowerCase()) ||
        c.toLowerCase().includes(buyerCountry.toLowerCase())
      );
    }
    
    return false;
  }, [planFeatures, user?.adminRole]);

  // Refresh subscription data
  const refreshSubscription = useCallback(() => {
    return fetchSubscription();
  }, [fetchSubscription]);

  const value = {
    subscription,
    planFeatures,
    planName,
    planDisplayName,
    loading,
    error,
    expiryStatus,
    hasPermission,
    getFeature,
    isPaidSubscriber,
    isAdmin,
    canViewContactsForRegion,
    refreshSubscription,
    DEFAULT_PLAN_FEATURES
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

export default SubscriptionContext;
